import { useEffect, useState } from "react";
import { Link, useSearchParams } from "react-router-dom";
import { trackDownload } from "../analytics";

// What the page knows about each platform it can hand out. The order is the
// order the cards appear in when nothing tells us which machine the reader is
// on; when we can tell, theirs comes first.
const PLATFORMS = {
  windows: {
    name: 'Windows',
    requirement: 'Windows 10 or later, 64-bit',
    kind: 'A single .exe. Nothing to install.',
    // How the file is checked once it has arrived. Windows has it built in.
    hashCommand: (filename) => `Get-FileHash .\\${filename}`,
    hashWhere: 'In PowerShell, from your Downloads folder',
    hashResult: 'The Hash column should match the checksum above exactly.',
    steps: [
      { title: 'Download', description: 'The .exe file will be saved to your Downloads folder.' },
      { title: 'Run', description: 'Double-click the file to run. No installation needed. Windows SmartScreen will warn about an unrecognised publisher the first time — the program is not code-signed.' },
      { title: 'Edit', description: 'Load a world and start editing. Backups are automatic.' },
    ],
  },
  linux: {
    name: 'Linux',
    requirement: '64-bit x86 desktop. Validated on Ubuntu 24.04',
    kind: 'A .tar.gz with the .NET runtime inside. Nothing to install.',
    hashCommand: (filename) => `sha256sum ~/Downloads/${filename}`,
    hashWhere: 'In a terminal',
    hashResult: 'The first column should match the checksum above exactly.',
    steps: [
      { title: 'Extract', description: 'Unpack the archive with your file manager, or tar -xzf from a terminal. It contains one folder with the program, a README and the licences.' },
      { title: 'Run', description: 'Open a terminal in that folder and run ./BedrockToolchest. If your extractor dropped the permission, chmod +x BedrockToolchest first. The README lists the system libraries Ubuntu needs if it does not start.' },
      { title: 'Edit', description: 'Load a world and start editing. Backups are automatic. Keep worlds on a local Linux filesystem rather than a Windows mount.' },
    ],
  },
};

/** Which of our platforms the browser is running on, or null if we cannot tell. */
function detectPlatform() {
  const ua = navigator.userAgent || '';
  if (/Windows/i.test(ua)) return 'windows';
  // Android is Linux under the hood but there is nothing for it here.
  if (/Linux/i.test(ua) && !/Android/i.test(ua)) return 'linux';
  return null;
}

/**
 * The downloadable files a release carries, one per platform. The Windows
 * fields live at the top level of the entry because they always have, and
 * every other reader of versions.json (the update API, the deploy script)
 * expects them there; Linux arrived in 3.0.3 as a nested object.
 */
function filesOf(release) {
  const files = [];
  if (release.filename) {
    files.push({
      platform: 'windows',
      version: release.version,
      filename: release.filename,
      sha256: release.sha256,
      size: release.size,
      virustotal: release.virustotal,
    });
  }
  if (release.linux && release.linux.filename) {
    files.push({ platform: 'linux', version: release.version, ...release.linux });
  }
  return files;
}

export default function Download() {
  const [searchParams] = useSearchParams();
  const [availableVersions, setAvailableVersions] = useState([]);
  const [latestVersion, setLatestVersion] = useState(null);
  // A release that is announced but not yet published. It carries no file, so it
  // must never reach the download buttons — it is here to be shown, not fetched.
  const [pendingVersion, setPendingVersion] = useState(null);
  const [isLoadingVersions, setIsLoadingVersions] = useState(true);
  // The platform the reader is on, or asked for. It decides which card leads,
  // which verify instructions show and which getting-started steps apply.
  const [platform, setPlatform] = useState(() => {
    const asked = searchParams.get('platform');
    if (asked && PLATFORMS[asked]) return asked;
    return detectPlatform() || 'windows';
  });
  const [detected] = useState(() => detectPlatform());
  // The file the user actually fetched, which is what the checksum below has
  // to describe. Null until a download starts, so the block shows the latest
  // for the chosen platform.
  const [downloadedFile, setDownloadedFile] = useState(null);
  const [copied, setCopied] = useState(null);

  const copyText = async (text, key) => {
    try {
      await navigator.clipboard.writeText(text);
    } catch {
      const area = document.createElement('textarea');
      area.value = text;
      document.body.appendChild(area);
      area.select();
      document.execCommand('copy');
      document.body.removeChild(area);
    }
    setCopied(key);
    setTimeout(() => setCopied(null), 2000);
  };

  // Fetch available versions
  useEffect(() => {
    const fetchAvailableVersions = async () => {
      try {
        const response = await fetch(`/downloads/versions.json?t=${Date.now()}`);
        if (response.ok) {
          const versions = await response.json();
          const downloadable = versions.filter(v => !v.pending);
          setAvailableVersions(downloadable);
          setPendingVersion(versions.find(v => v.pending) || null);
          const latest = downloadable.find(v => v.latest) || downloadable[0];
          setLatestVersion(latest);
        } else {
          throw new Error('versions.json not found');
        }
      } catch (error) {
        console.log('Using fallback version detection');
        const fallbackVersion = {
          version: 'v2.8.7',
          filename: 'BedrockToolchest_v287.exe',
          date: '2026-04-26',
          latest: true
        };
        setAvailableVersions([fallbackVersion]);
        setLatestVersion(fallbackVersion);
      } finally {
        setIsLoadingVersions(false);
      }
    };
    fetchAvailableVersions();
  }, []);

  const triggerFileDownload = (file) => {
    setDownloadedFile(file);
    setPlatform(file.platform);
    const link = document.createElement('a');
    link.href = `/downloads/${file.filename}`;
    link.download = file.filename;
    document.body.appendChild(link);
    link.click();
    document.body.removeChild(link);
  };

  const startDownload = async (file) => {
    if (!file) return;
    // Each click is its own download and its own line in the stats, so the
    // Windows and Linux counts stay honest. Tracking must never hold the file up.
    try {
      await trackDownload({
        downloadMethod: 'manual',
        version: file.version,
        filename: file.filename,
        platform: file.platform,
      });
    } catch (error) {
      console.error('Error tracking download:', error);
    }
    triggerFileDownload(file);
  };

  const latestFiles = latestVersion ? filesOf(latestVersion) : [];
  // The reader's platform leads; the rest follow in our own order.
  const orderedFiles = [
    ...latestFiles.filter(f => f.platform === platform),
    ...latestFiles.filter(f => f.platform !== platform),
  ];
  const verifyFile = downloadedFile || latestFiles.find(f => f.platform === platform) || latestFiles[0];
  const info = PLATFORMS[platform];

  return (
    <div className="max-w-3xl mx-auto px-4 sm:px-6 py-16 md:py-24">
      {/* Header */}
      <div className="text-center mb-10">
        <h1 className="text-3xl md:text-4xl font-bold text-slate-900 dark:text-white mb-3">
          Download Bedrock Toolchest
        </h1>
        <p className="text-slate-600 dark:text-slate-400">
          {latestVersion ? `${latestVersion.version}, released ${latestVersion.date}. ` : ''}
          Free, one file, nothing to install. Pick your platform.
        </p>
      </div>

      {/* An announced release that has not been published yet */}
      {pendingVersion && (
        <div className="mb-8 rounded-xl border border-primary-200 dark:border-primary-800/50 bg-primary-50 dark:bg-primary-900/20 p-5">
          <h2 className="font-semibold text-primary-800 dark:text-primary-300 mb-2">
            {pendingVersion.version} is on the way
          </h2>
          <p className="text-sm text-primary-700 dark:text-primary-400">
            You can read what is in it in the{' '}
            <Link to="/changelog" className="underline">changelog</Link> and{' '}
            <Link to="/docs" className="underline">the documentation</Link> now. The
            downloads below are {latestVersion ? latestVersion.version : 'the current release'}.
          </p>
        </div>
      )}

      {/* The platform cards */}
      <div className="relative mb-12 pt-6 sm:pt-0">
        {/* Avatar - overlapping left side of card */}
        <img
          src={downloadedFile ? '/images/avatars/download_started_avatar.png' : '/images/avatars/download_starting_avatar.png'}
          alt=""
          className="hidden sm:block absolute -left-12 md:-left-20 top-1/2 -translate-y-1/2 w-48 h-48 md:w-60 md:h-60 object-contain drop-shadow-lg pointer-events-none z-10"
        />
        {/* Mobile avatar - centered above card */}
        <img
          src={downloadedFile ? '/images/avatars/download_started_avatar.png' : '/images/avatars/download_starting_avatar.png'}
          alt=""
          className="sm:hidden mx-auto -mb-6 w-36 h-36 object-contain drop-shadow-lg pointer-events-none relative z-10"
        />

        <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-slate-50 dark:bg-slate-900 p-6 sm:pl-36 md:pl-40">
          {isLoadingVersions ? (
            <div className="text-center text-slate-500 dark:text-slate-500 py-6">
              Finding the latest release&hellip;
            </div>
          ) : downloadedFile ? (
            <div className="text-center mb-5">
              <div className="text-2xl font-bold text-green-700 dark:text-green-400 mb-1">Download started</div>
              <div className="text-sm text-slate-600 dark:text-slate-400">
                <span className="font-mono">{downloadedFile.filename}</span> should be arriving in your Downloads folder.
                If it has not,{' '}
                <button
                  onClick={() => triggerFileDownload(downloadedFile)}
                  className="text-primary-500 hover:text-primary-600 dark:hover:text-primary-400 underline"
                >
                  click here
                </button>.
              </div>
            </div>
          ) : null}

          {!isLoadingVersions && (
            <div className="space-y-3">
              {orderedFiles.map((file) => {
                const p = PLATFORMS[file.platform];
                const lead = file.platform === platform;
                return (
                  <div
                    key={file.platform}
                    className={`rounded-lg border p-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-3 ${
                      lead
                        ? 'border-primary-300 dark:border-primary-700/60 bg-white dark:bg-slate-950'
                        : 'border-slate-200 dark:border-slate-800 bg-white/60 dark:bg-slate-950/40'
                    }`}
                  >
                    <div className="min-w-0">
                      <div className="flex items-center gap-2 mb-0.5">
                        <span className="font-semibold text-slate-900 dark:text-white">{p.name}</span>
                        {detected === file.platform && (
                          <span className="px-2 py-0.5 rounded-full text-[10px] font-semibold uppercase tracking-wide bg-primary-400/15 text-primary-600 dark:text-primary-400">
                            Your platform
                          </span>
                        )}
                      </div>
                      <div className="text-xs text-slate-500 dark:text-slate-500">
                        {p.requirement} &middot; {p.kind}
                      </div>
                      <div className="text-xs font-mono text-slate-500 dark:text-slate-500 mt-1 break-all">
                        {file.filename}
                        {file.size && (
                          <span className="ml-2 font-sans">{(file.size / 1048576).toFixed(1)} MB</span>
                        )}
                      </div>
                    </div>
                    <button
                      onClick={() => startDownload(file)}
                      className={`px-5 py-2.5 rounded-lg font-medium transition-colors shrink-0 ${
                        lead
                          ? 'bg-primary-400 hover:bg-primary-500 text-white'
                          : 'bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300'
                      }`}
                    >
                      Download for {p.name}
                    </button>
                  </div>
                );
              })}
              {orderedFiles.length === 0 && (
                <div className="text-center text-slate-500 dark:text-slate-500 py-4">
                  No downloads are available right now.
                </div>
              )}
            </div>
          )}
        </div>
      </div>

      {/* Verify the download. The checksum lives beside the file it describes,
          so it proves the bytes arrived intact and are the ones we published -
          not that this site is who it says it is; that is what the VirusTotal
          report and, one day, a code signature are for. The report link is
          shown only when a release carries one, so it never points at an
          empty page. */}
      {(() => {
        const v = verifyFile;
        if (!v || !v.sha256) return null;
        const p = PLATFORMS[v.platform];
        const vtUrl = v.virustotal === true
          ? `https://www.virustotal.com/gui/file/${v.sha256}`
          : v.virustotal || null;
        const command = p.hashCommand(v.filename);
        return (
          <div className="mb-12">
            <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-1">
              Verify your download
            </h2>
            <p className="text-sm text-slate-600 dark:text-slate-400 mb-4">
              {v.platform === 'windows'
                ? 'Bedrock Toolchest is not code-signed, so Windows SmartScreen will warn about an unrecognised publisher the first time you run it. '
                : ''}
              You can confirm the file you downloaded is the one we published by comparing
              its SHA-256 checksum.
            </p>
            <div className="rounded-xl border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 divide-y divide-slate-200 dark:divide-slate-800">
              <div className="p-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm">
                <span className="text-slate-500 dark:text-slate-500">File</span>
                <span className="font-mono text-slate-800 dark:text-slate-200 break-all">
                  {v.filename}
                  {v.size && (
                    <span className="ml-2 font-sans text-slate-500 dark:text-slate-500">
                      {(v.size / 1048576).toFixed(1)} MB
                    </span>
                  )}
                </span>
              </div>
              <div className="p-4 text-sm">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-slate-500 dark:text-slate-500">SHA-256</span>
                  <button
                    onClick={() => copyText(v.sha256, 'sha')}
                    className="text-xs text-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
                  >
                    {copied === 'sha' ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <code className="block font-mono text-xs break-all text-slate-800 dark:text-slate-200 select-all">
                  {v.sha256}
                </code>
              </div>
              <div className="p-4 text-sm">
                <div className="flex items-center justify-between mb-1.5">
                  <span className="text-slate-500 dark:text-slate-500">{p.hashWhere}</span>
                  <button
                    onClick={() => copyText(command, 'cmd')}
                    className="text-xs text-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
                  >
                    {copied === 'cmd' ? 'Copied' : 'Copy'}
                  </button>
                </div>
                <code className="block font-mono text-xs break-all text-slate-800 dark:text-slate-200 select-all">
                  {command}
                </code>
                <p className="mt-1.5 text-xs text-slate-500 dark:text-slate-500">{p.hashResult}</p>
              </div>
              {vtUrl && (
                <div className="p-4 flex flex-wrap items-center justify-between gap-x-4 gap-y-1 text-sm">
                  <span className="text-slate-500 dark:text-slate-500">Independent scan</span>
                  <a
                    href={vtUrl}
                    target="_blank"
                    rel="noopener noreferrer"
                    className="text-primary-500 hover:text-primary-600 dark:hover:text-primary-400"
                  >
                    VirusTotal report for this file &rarr;
                  </a>
                </div>
              )}
            </div>
          </div>
        );
      })()}

      {/* Installation Steps */}
      <div className="mb-12">
        <div className="flex flex-wrap items-center justify-between gap-3 mb-4">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white">
            Getting started on {info.name}
          </h2>
          <div className="inline-flex rounded-lg border border-slate-200 dark:border-slate-800 p-0.5 text-xs">
            {Object.entries(PLATFORMS).map(([key, p]) => (
              <button
                key={key}
                onClick={() => setPlatform(key)}
                className={`px-3 py-1 rounded-md transition-colors ${
                  platform === key
                    ? 'bg-primary-400 text-white'
                    : 'text-slate-600 dark:text-slate-400 hover:text-slate-900 dark:hover:text-white'
                }`}
              >
                {p.name}
              </button>
            ))}
          </div>
        </div>
        <div className="grid grid-cols-1 md:grid-cols-3 gap-4">
          {info.steps.map(({ title, description }, i) => (
            <div key={title} className="p-5 rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900">
              <div className="w-8 h-8 rounded-full bg-primary-50 dark:bg-primary-400/10 text-primary-500 dark:text-primary-400 flex items-center justify-center font-bold text-sm mb-3">
                {i + 1}
              </div>
              <h3 className="font-semibold text-slate-900 dark:text-white mb-1">{title}</h3>
              <p className="text-sm text-slate-600 dark:text-slate-400">{description}</p>
            </div>
          ))}
        </div>
      </div>

      {/* Older Versions */}
      {!isLoadingVersions && availableVersions.length > 1 && (
        <div className="mb-12">
          <h2 className="text-xl font-semibold text-slate-900 dark:text-white mb-4">
            Older Versions
          </h2>
          <div className="grid grid-cols-1 sm:grid-cols-2 lg:grid-cols-3 gap-3">
            {availableVersions
              .filter(version => !version.latest)
              .map((version) => (
              <div key={version.version} className="rounded-lg border border-slate-200 dark:border-slate-800 bg-white dark:bg-slate-900 p-4">
                <div className="flex items-center justify-between mb-3">
                  <span className="font-medium text-slate-900 dark:text-white">{version.version}</span>
                  <span className="text-xs text-slate-500 dark:text-slate-500">{version.date}</span>
                </div>
                <div className="space-y-2">
                  {filesOf(version).map((file) => (
                    <div key={file.platform}>
                      <button
                        onClick={() => startDownload(file)}
                        className="w-full px-3 py-1.5 bg-slate-100 dark:bg-slate-800 hover:bg-slate-200 dark:hover:bg-slate-700 text-slate-700 dark:text-slate-300 rounded-lg text-sm transition-colors"
                      >
                        {PLATFORMS[file.platform].name}
                      </button>
                      {file.sha256 && (
                        <button
                          onClick={() => copyText(file.sha256, `${version.version}-${file.platform}`)}
                          title={file.sha256}
                          className="mt-1 w-full text-xs text-slate-500 hover:text-primary-500 dark:hover:text-primary-400"
                        >
                          {copied === `${version.version}-${file.platform}` ? 'Copied' : 'Copy SHA-256'}
                        </button>
                      )}
                    </div>
                  ))}
                </div>
              </div>
            ))}
          </div>
        </div>
      )}

      {/* Links */}
      <div className="flex flex-wrap justify-center gap-4 text-sm">
        <Link to="/docs" className="text-primary-500 hover:text-primary-600 dark:hover:text-primary-400">
          Read the docs
        </Link>
        <Link to="/changelog" className="text-primary-500 hover:text-primary-600 dark:hover:text-primary-400">
          View changelog
        </Link>
        <Link to="/" className="text-slate-500 hover:text-slate-700 dark:hover:text-slate-400">
          Back to home
        </Link>
      </div>
    </div>
  );
}
